import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Text } from "@amzn/stencil-react-components/text";
import { FormattedMessage } from "react-intl";

import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";

/**
 * Props for the SecurityKeyTestingSuccessScreen component.
 */
interface SecurityKeyTestingSuccessScreenProps {
  /** Callback function to proceed to the next step in the workflow */
  onNext: () => void;
}

/**
 * Success screen component displayed after security key OTP validation succeeds.
 *
 * This component shows a confirmation screen when the user's security key has been
 * successfully tested and validated. It displays a success message banner and provides
 * a navigation option to proceed to the next step in the onboarding workflow.
 * The back button is intentionally hidden after successful validation to prevent
 * users from navigating back and potentially causing confusion.
 *
 * @param props - The component props
 * @param props.onNext - Function called when user clicks the next button to continue
 * @returns JSX element containing the success screen with next navigation option
 */
export function SecurityKeyTestingSuccessScreen({
  onNext,
}: SecurityKeyTestingSuccessScreenProps): JSX.Element {
  return (
    <Col rowGap="S400" dataTestId="securityKeySuccessScreen">
      <StepBodyStyledText>
        <Col rowGap="S400">
          <Text
            fontSize="T300"
            fontWeight="bold"
            dataTestId="securityKeyTestingHeading"
          >
            <FormattedMessage id="insertAndTestSecurityKey-otpInput-testingHeading" />
          </Text>

          <MessageBanner
            type={MessageBannerType.Success}
            dataTestId="securityKeySuccessBanner"
          >
            <FormattedMessage id="insertAndTestSecurityKey-success-successMessage" />
          </MessageBanner>

          <Row justifyContent="flex-end">
            <Button
              variant={ButtonVariant.Primary}
              onClick={onNext}
              dataTestId="securityKeySuccessNextButton"
            >
              <FormattedMessage id="button-next" />
            </Button>
          </Row>
        </Col>
      </StepBodyStyledText>
    </Col>
  );
}
